import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useContextHook } from 'use-context-hook';
import { io } from 'socket.io-client';
import { AuthContext } from '../../Context/authContext';
import automationService from '../../services/automationService';
import linkedinService from '../../services/linkedinService';
import domainService from '../../services/domainService';
import CenterModal from '../molecules/Modal/CenterModal';
import LeadsDetail from '../atoms/LeadsDetails';
import { getCookie } from '../../helpers/common';
import {
  FlowBuilderContainer,
  Header,
  HeaderLeft,
  BackButton,
  TitleSection,
  WorkflowTitle,
  EditIcon,
  HeaderCenter,
  ProgressStepper,
  StepItem,
  StepLine,
  HeaderRight,
  SaveDraftButton,
  NextButton,
  ToastContainer,
  Toast,
  ToastContent,
  ToastClose,
  LaunchButton,
  ClickableStepItem,
  AddLeadsButton,
  LeadsModalBody,
} from './FlowBuilder.styles';
import { RiArrowLeftSLine } from 'react-icons/ri';
import { FiEdit } from 'react-icons/fi';
import { CgClose } from 'react-icons/cg';
import {
  ACTION_NODES,
  MAX_ZOOM,
  MESSAGE_LIMITS,
  MESSAGE_VARIABLES,
  MIN_ZOOM,
  getCascadeDeleteConfirmMessage,
  getCascadeDeleteImpact,
  getNodeStepValidationError,
  pruneOrphanDependentNodes,
  removeNodeAndFollowing,
  workflowHasNodeType,
  ZOOM_STEP,
} from './constants';
import FlowBuilderCanvas from './FlowBuilderCanvas';
import FlowBuilderActionsPanel from './FlowBuilderActionsPanel';
import FlowBuilderPropertiesPanel from './FlowBuilderPropertiesPanel';
import FlowBuilderLaunch from './FlowBuilderLaunch';
import ExecutionsDrawer from './ExecutionsDrawer';
import { isEmailNodeType, getEmailScheduleFromNode, isActiveEmailSchedule } from './emailScheduleUtils';

const SEND_EMAIL_ENVS = ['staging-micro', 'local'];
const HIDDEN_ACTION_TYPES = ['check_google_calendar_meeting'];
const VISIBLE_ACTION_NODES = SEND_EMAIL_ENVS.includes(process.env.REACT_APP_ENV)
  ? ACTION_NODES
  : ACTION_NODES.filter(n => !HIDDEN_ACTION_TYPES.includes(n.type));
const HIDE_DAILY_LIMIT_ENVS = ['production', 'production-micro'];
const SHOW_DAILY_LIMIT_FIELD = !HIDE_DAILY_LIMIT_ENVS.includes(process.env.REACT_APP_ENV);
const PAN_EDGE_PADDING = 24;
const PAN_HORIZONTAL_ALLOWANCE = 360;

// Generate unique ID
const generateId = () => 'node_' + Math.random().toString(36).slice(2, 11);

const FlowBuilder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const templateAppliedRef = useRef(false);
  const titleInputRef = useRef(null);
  const canvasRef = useRef(null);

  const { fetch, refetch, user } = useContextHook(AuthContext, v => ({
    fetch: v.fetch,
    refetch: v.refetch,
    user: v.user,
  }));

  // State
  const [workflowId, setWorkflowId] = useState(null);
  const [workflowName, setWorkflowName] = useState(
    () => `New workflow ${Math.random().toString(36).slice(2, 8)}`,
  );
  const [nodes, setNodes] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [propertiesPanelOpen, setPropertiesPanelOpen] = useState(false);
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  const [currentStep, setCurrentStep] = useState(() => (searchParams.get('step') === 'launch' ? 'launch' : 'sequence'));
  const [leadList, setLeadList] = useState('');
  const [actionsPanelOpen, setActionsPanelOpen] = useState(false);
  const [activeAddButtonId, setActiveAddButtonId] = useState(null);

  // Execution tracking state
  const [nodeStats, setNodeStats] = useState({}); // { [nodeId]: { completed, failed, inProgress } }
  const [analyticsData, setAnalyticsData] = useState({}); // { [nodeId]: { sent, opened, replied, accepted } }
  const [workflowStatus, setWorkflowStatus] = useState('draft'); // draft, active, completed, failed
  const [totalExecutions, setTotalExecutions] = useState(0);
  const [completedExecutions, setCompletedExecutions] = useState(0);
  const [failedExecutions, setFailedExecutions] = useState(0);
  const [runningExecutions, setRunningExecutions] = useState(0);
  const statsPollingRef = useRef(null);
  const automationSocketRef = useRef(null);

  // Executions drawer state
  const [executionsDrawer, setExecutionsDrawer] = useState({ open: false, nodeId: null, nodeLabel: null, status: 'all' });
  const openExecutionsDrawer = useCallback(({ nodeId, nodeLabel, status }) => {
    setPropertiesPanelOpen(false);
    setActionsPanelOpen(false);
    setActiveAddButtonId(null);
    setExecutionsDrawer({ open: true, nodeId: nodeId || null, nodeLabel: nodeLabel || null, status: status || 'all' });
  }, []);

  // Voice recorder state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const audioPlayerRef = useRef(null);

  // Launch page state
  const [description, setDescription] = useState('');
  const [timezone, setTimezone] = useState(() => {
    // Auto-detect timezone from browser
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      // Must be a real IANA value present in the timezones list, otherwise the
      // Time Zone select resolves to null and renders blank.
      return 'America/New_York';
    }
  });
  const [timeFrom, setTimeFrom] = useState('09:00 AM');
  const [timeTo, setTimeTo] = useState('04:00 PM');
  const [selectedDays, setSelectedDays] = useState([]);
  const [startingDate, setStartingDate] = useState(() => {
    const today = new Date();
    return today.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  });
  const [dailyLimit, setDailyLimit] = useState('');
  const [activeProfiles, setActiveProfiles] = useState({});
  const [isIframeOpen, setIsIframeOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deletePending, setDeletePending] = useState(null);
  const [leadsModalOpen, setLeadsModalOpen] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState({});
  const [variablesDropdownOpen, setVariablesDropdownOpen] = useState(false);
  const messageTextareaRef = useRef(null);

  // Fetch LinkedIn profiles dynamically
  const [profileSearchQuery] = useState({
    page: 1,
    itemsPerPage: 50,
    searchText: '',
    getAll: false,
    startDate: '',
    endDate: '',
    status: 'all',
  });
  const { userProfile_loading, userProfile_data } = linkedinService.GetAllUserProfileData(profileSearchQuery, fetch);

  const { emails_data } = domainService.GetAllEmails(
    { page: 1, itemsPerPage: 100, searchText: '', startDate: '', endDate: '', domain: '' },
    fetch,
  );
  const emailAccounts = emails_data?.data?.items || [];

  const hasLinkedInProfiles = useMemo(() => {
    if (userProfile_loading) return true;
    return Array.isArray(userProfile_data) && userProfile_data.length > 0;
  }, [userProfile_loading, userProfile_data]);

  const [workflowLinkedProfileIds, setWorkflowLinkedProfileIds] = useState(null);

  // Initialize active profiles when data loads
  useEffect(() => {
    if (userProfile_data && userProfile_data.length > 0) {
      const initialActive = {};
      if (workflowLinkedProfileIds) {
        // Workflow was loaded — only activate the workflow's linked profiles
        userProfile_data.forEach(profile => {
          initialActive[profile.id] = workflowLinkedProfileIds.has(profile.id);
        });
      } else {
        // New workflow — default to logged-in profiles
        userProfile_data.forEach(profile => {
          initialActive[profile.id] = profile.isLoggedIn === true;
        });
      }
      setActiveProfiles(initialActive);
    }
  }, [userProfile_data, workflowLinkedProfileIds]);

  const toggleDay = day => {
    setSelectedDays(prev => (prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]));
  };

  const toggleProfile = id => {
    setActiveProfiles(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleLeadsSelected = leadsMap => {
    // If workflow is active or failed, add leads to the running/existing workflow
    if (workflowStatus === 'active' || workflowStatus === 'failed') {
      // Filter only truly new leads (not already in selectedLeads)
      const newLeads = {};
      // selectedLeads is an object keyed by lead id; filter out leads already in the workflow
      Object.entries(leadsMap).forEach(([key, value]) => {
        if (!selectedLeads[key]) {
          newLeads[key] = value;
        }
      });
      setLeadsModalOpen(false);
      if (Object.keys(newLeads).length > 0) {
        handleAddLeadsToRunning(newLeads);
      } else {
        showToast('info', 'No New Leads', 'All selected leads are already in the workflow');
      }
      return;
    }

    setSelectedLeads(leadsMap);
    const count = Object.keys(leadsMap).length;
    setLeadList(`${count} lead${count !== 1 ? 's' : ''} selected`);
    setLeadsModalOpen(false);
    showToast('success', 'Leads Added', `${count} lead${count !== 1 ? 's' : ''} added to automation`);
  };

  const selectedLeadsCount = Object.keys(selectedLeads).length;

  // ─── Execution stats fetching ─────────────────────────────
  const fetchWorkflowStats = useCallback(async () => {
    if (!workflowId) return;
    try {
      const [statsData, analyticsRes] = await Promise.allSettled([
        automationService.getWorkflowStats(workflowId),
        automationService.getWorkflowAnalytics(workflowId),
      ]);

      if (statsData.status === 'fulfilled' && statsData.value?.success && statsData.value?.data) {
        const d = statsData.value.data;
        setNodeStats(d.nodeStats || {});
        setTotalExecutions(d.totalExecutions || 0);
        setCompletedExecutions(d.completedExecutions || 0);
        setFailedExecutions(d.failedExecutions || 0);
        setRunningExecutions(d.runningExecutions || 0);

        if (d.runningExecutions > 0) {
          setWorkflowStatus('active');
        } else if (d.totalExecutions > 0 && d.runningExecutions === 0) {
          if (d.failedExecutions > 0 && d.completedExecutions === 0) {
            setWorkflowStatus('failed');
          } else {
            setWorkflowStatus('completed');
          }
        }
      }

      if (analyticsRes.status === 'fulfilled' && analyticsRes.value?.success && analyticsRes.value?.data) {
        setAnalyticsData(analyticsRes.value.data.nodeAnalytics || {});
      }
    } catch (error) {
      console.error('Failed to fetch workflow stats:', error);
    }
  }, [workflowId]);

  // Helper to get node stats (looks in flat stats and falls back to 0)
  const getNodeStats = useCallback(
    nodeId => {
      return nodeStats[nodeId] || { completed: 0, failed: 0, inProgress: 0 };
    },
    [nodeStats],
  );

  // Fetch stats when workflow loads and start polling when active
  useEffect(() => {
    if (workflowId) {
      fetchWorkflowStats();
    }
  }, [workflowId, fetchWorkflowStats]);

  // Polling: fetch stats every 5 seconds when workflow is active or failed
  useEffect(() => {
    if ((workflowStatus === 'active' || workflowStatus === 'failed') && workflowId) {
      statsPollingRef.current = setInterval(fetchWorkflowStats, 5000);
    }
    return () => {
      if (statsPollingRef.current) {
        clearInterval(statsPollingRef.current);
        statsPollingRef.current = null;
      }
    };
  }, [workflowStatus, workflowId, fetchWorkflowStats]);

  // Toast helper
  const showToast = useCallback((type, title, message) => {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev, { id, type, title, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  }, []);

  // ─── Socket.io connection for real-time updates ───────────
  useEffect(() => {
    const automationUrl = process.env.REACT_APP_AUTOMATION_API_URL;
    if (!automationUrl || !workflowId) return;

    // Derive the socket URL from the API URL (strip /automation/api/v1)
    const socketUrl = automationUrl.replace(/\/automation\/api\/v1\/?$/, '');
    const token = getCookie(process.env.REACT_APP_AI_TOKEN_COOKIE);

    try {
      const sock = io(socketUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 3000,
      });

      // Listen for per-action progress
      sock.on('workflow:action_completed', data => {
        if (data.workflowId !== workflowId) return;
        // Refresh stats on each action completion
        fetchWorkflowStats();
      });

      // Listen for execution completion
      sock.on('workflow:execution_done', data => {
        if (data.workflowId !== workflowId) return;
        fetchWorkflowStats();
        if (data.status === 'completed') {
          showToast('success', 'Execution Done', `Completed for ${data.targetProfileName || data.targetProfileId}`);
        } else if (data.status === 'failed') {
          showToast('error', 'Execution Failed', `Failed for ${data.targetProfileName || data.targetProfileId}`);
        }
      });

      // Listen for workflow launched
      sock.on('workflow:launched', data => {
        if (data.workflowId !== workflowId) return;
        setWorkflowStatus('active');
        fetchWorkflowStats();
      });

      automationSocketRef.current = sock;
    } catch (err) {
      console.error('Failed to connect automation socket:', err);
      showToast('error', 'Connection Error', 'Real-time updates unavailable');
    }

    return () => {
      if (automationSocketRef.current) {
        automationSocketRef.current.disconnect();
        automationSocketRef.current = null;
      }
    };
  }, [workflowId, fetchWorkflowStats, showToast]);

  // Pan & Zoom state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0 });
  const panOffset = useRef({ x: 0, y: 0 });

  const clampPan = useCallback(
    nextPan => {
      const canvasEl = canvasRef.current;
      const contentEl = canvasEl?.firstElementChild;
      if (!canvasEl || !contentEl) return nextPan;

      const viewportWidth = canvasEl.clientWidth;
      const viewportHeight = canvasEl.clientHeight;
      // The flow is center-aligned, so wide branch rows overflow equally on both
      // sides — but scrollWidth only measures the rightward overflow (leftward
      // overflow can't be scrolled to, so it's excluded). Mirror the right overflow
      // to recover the true symmetric width; otherwise the pan range is ~half of
      // what's needed and the left/right edges of wide workflows stay unreachable.
      const rightOverflow = Math.max(0, contentEl.scrollWidth - contentEl.clientWidth);
      const contentWidth = (contentEl.clientWidth + rightOverflow * 2) * zoom;
      const contentHeight = contentEl.scrollHeight * zoom;

      // Horizontal content is center-aligned, so overflow exists on both sides.
      // Allow panning equally to left/right so edge branches remain reachable.
      // Branch arms and analytics widgets extend visually outside normal flow width
      // (absolute positioning), so include extra allowance to keep right edge reachable.
      const horizontalOverflow = Math.max(
        0,
        contentWidth + PAN_HORIZONTAL_ALLOWANCE * 2 - viewportWidth,
      );
      const horizontalRange = horizontalOverflow / 2;
      const minX = -horizontalRange - PAN_EDGE_PADDING;
      const maxX = horizontalRange + PAN_EDGE_PADDING;

      // Keep top anchored at Automation Start (no extra upward movement),
      // while still allowing downward traversal to lower nodes.
      const minY = Math.min(PAN_EDGE_PADDING, viewportHeight - contentHeight - PAN_EDGE_PADDING);
      const maxY = 0;

      return {
        x: Math.min(maxX, Math.max(minX, nextPan.x)),
        y: Math.min(maxY, Math.max(minY, nextPan.y)),
      };
    },
    [zoom],
  );

  // Load workflow if ID in URL
  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      loadWorkflow(id);
    }
  }, [searchParams]);

  // Seed canvas from a template selected in the campaign creation flow.
  // Only applies on first mount when no workflow id is in the URL, so editing
  // an existing workflow never gets clobbered.
  useEffect(() => {
    if (templateAppliedRef.current) return;
    if (searchParams.get('id')) return;
    const tpl = location.state;
    if (!tpl || !Array.isArray(tpl.templateNodes) || tpl.templateNodes.length === 0) return;
    templateAppliedRef.current = true;
    setNodes(tpl.templateNodes);
    if (tpl.templateName) setWorkflowName(tpl.templateName);
    showToast('success', 'Template Loaded', `Loaded "${tpl.templateName || 'template'}"`);
    // Clear the navigation state so a refresh doesn't re-seed the template.
    navigate({ pathname: location.pathname, search: location.search }, { replace: true });
  }, [location, navigate, searchParams, showToast]);

  const loadWorkflow = async id => {
    try {
      const data = await automationService.getWorkflow(id);
      if (data.success && data.data) {
        const workflow = data.data;
        setWorkflowId(workflow.id);
        setWorkflowName(workflow.name);
        setDescription(workflow.description || '');
        setNodes(workflow.nodes || []);
        if (workflow.status) {
          setWorkflowStatus(workflow.status);
        }

        // Restore linked LinkedIn profiles from workflow.
        // Only update the linked-id set here — the effect above owns activeProfiles
        // and will re-fire once both this set and userProfile_data are available.
        if (workflow.linkedinProfiles && workflow.linkedinProfiles.length > 0) {
          const linkedIds = new Set(
            workflow.linkedinProfiles
              .map(x => (typeof x === 'object' && x != null ? x.id : x))
              .filter(Boolean),
          );
          setWorkflowLinkedProfileIds(linkedIds);
        }

        // Restore selected leads
        const rawLeadIds = workflow.userLeads || workflow.userLeadIds || [];
        if (rawLeadIds.length > 0) {
          const leadsMap = {};
          rawLeadIds.forEach(lead => {
            if (typeof lead === 'object' && lead !== null) {
              const id = lead.id || lead.userLeadId;
              if (id != null) leadsMap[id] = lead;
            } else {
              leadsMap[lead] = { id: lead };
            }
          });
          setSelectedLeads(leadsMap);
          const count = Object.keys(leadsMap).length;
          setLeadList(`${count} lead${count !== 1 ? 's' : ''} selected`);
        }

        const s = workflow.settings || {};
        if (s.timezone) setTimezone(s.timezone);
        if (s.timeFrom) setTimeFrom(s.timeFrom);
        if (s.timeTo) setTimeTo(s.timeTo);
        if (Array.isArray(s.days)) setSelectedDays(s.days);
        if (s.startingDate) setStartingDate(s.startingDate);
        if (s.dailyLimit !== undefined && s.dailyLimit !== null) setDailyLimit(String(s.dailyLimit));

        showToast('success', 'Workflow Loaded', `Loaded "${workflow.name}"`);
      }
    } catch (error) {
      console.error('Load error:', error);
      showToast('error', 'Load Failed', 'Failed to load workflow');
    }
  };

  // All possible branch data keys across every branching node type
  const ALL_BRANCH_KEYS = ['yesBranch', 'noBranch', 'repliedBranch', 'clickedBranch', 'openedBranch', 'nothingBranch'];

  const emailNodes = useMemo(() => {
    const found = [];
    const collect = list => {
      (list || []).forEach(n => {
        if (isEmailNodeType(n.type)) found.push(n);
        ALL_BRANCH_KEYS.forEach(k => {
          if (n.data?.[k]) collect(n.data[k]);
        });
      });
    };
    collect(nodes);
    return found;
  }, [nodes]);

  const hasSendEmailNode = emailNodes.length > 0;

  // Recursive tree walkers — handle arbitrarily nested branches
  const findNodeInList = (list, nodeId) => {
    for (const n of list || []) {
      if (n.id === nodeId) return n;
      for (const k of ALL_BRANCH_KEYS) {
        if (n.data?.[k]) {
          const found = findNodeInList(n.data[k], nodeId);
          if (found) return found;
        }
      }
    }
    return null;
  };

  const findNodeById = nodeId => findNodeInList(nodes, nodeId);

  const mapNodesDeep = (list, fn) =>
    (list || []).map(n => {
      const mapped = fn(n);
      if (!mapped) return mapped;
      const hasBranches = ALL_BRANCH_KEYS.some(k => mapped.data?.[k] !== undefined);
      if (!hasBranches) return mapped;
      const updatedData = { ...mapped.data };
      ALL_BRANCH_KEYS.forEach(k => {
        if (updatedData[k] !== undefined) updatedData[k] = mapNodesDeep(updatedData[k], fn);
      });
      return { ...mapped, data: updatedData };
    });

  const removeNodeRecursive = (list, nodeId) =>
    (list || [])
      .filter(n => n.id !== nodeId)
      .map(n => {
        const hasBranches = ALL_BRANCH_KEYS.some(k => n.data?.[k] !== undefined);
        if (!hasBranches) return n;
        const updatedData = { ...n.data };
        ALL_BRANCH_KEYS.forEach(k => {
          if (updatedData[k] !== undefined) updatedData[k] = removeNodeRecursive(updatedData[k], nodeId);
        });
        return { ...n, data: updatedData };
      });

  const updateNodeRecursive = (list, nodeId, key, value) =>
    mapNodesDeep(list, n => (n.id === nodeId ? { ...n, data: { ...n.data, [key]: value } } : n));

  const insertIntoBranchRecursive = (list, parentNodeId, branchKey, branchInsertIndex, newNode) =>
    mapNodesDeep(list, n => {
      if (n.id !== parentNodeId) return n;
      const branchCopy = [...(n.data?.[branchKey] || [])];
      if (branchInsertIndex != null) branchCopy.splice(branchInsertIndex, 0, newNode);
      else branchCopy.push(newNode);
      return { ...n, data: { ...n.data, [branchKey]: branchCopy } };
    });

  // Add node from action panel at a specific position
  const addNode = (nodeType, insertIndex = null, branchInfo = null) => {
    const nodeConfig = ACTION_NODES.find(n => n.type === nodeType);
    if (!nodeConfig) return;

    // if (!hasLinkedInProfiles) {
    //   showToast(
    //     'error',
    //     'LinkedIn profile required',
    //     'Connect a LinkedIn account before adding workflow steps.',
    //   );
    //   return;
    // }

    if (
      nodeType === 'send_linkedin_ai_message' &&
      (!userProfile_data || userProfile_data.length === 0)
    ) {
      showToast(
        'error',
        'LinkedIn profile required',
        'Connect a LinkedIn account before adding Send AI LinkedIn Message.',
      );
      return;
    }

    if (
      (nodeType === 'send_email' || nodeType === 'send_ai_email') &&
      emailAccounts.length === 0
    ) {
      showToast(
        'error',
        'Email account required',
        'Attach an email account before adding this email step.',
      );
      return;
    }

    if (nodeConfig.requiresNodeType && !workflowHasNodeType(nodes, nodeConfig.requiresNodeType)) {
      const requiredLabel =
        ACTION_NODES.find(n => n.type === nodeConfig.requiresNodeType)?.label || nodeConfig.requiresNodeType;
      showToast('error', 'Required Step Missing', `Add a ${requiredLabel} step before adding ${nodeConfig.label}.`);
      return;
    }

    const newNode = {
      id: generateId(),
      type: nodeType,
      label: nodeConfig.label,
      data:
        nodeType === 'wait_for_connection'
          ? { yesBranch: [], noBranch: [], maxWaitUnit: 'days' }
          : nodeType === 'check_connection' ||
            nodeType === 'email_finder' /* ||
              nodeType === 'check_stripe_customer' */
            ? { yesBranch: [], noBranch: [] }
            : nodeType === 'check_email_status'
              ? { repliedBranch: [], clickedBranch: [], openedBranch: [], nothingBranch: [] }
              : {},
    };

    if (branchInfo) {
      const { parentNodeId, branch, branchInsertIndex } = branchInfo;
      const branchKeyMap = {
        yes: 'yesBranch', no: 'noBranch',
        replied: 'repliedBranch', clicked: 'clickedBranch',
        opened: 'openedBranch', nothing: 'nothingBranch',
      };
      const branchKey = branchKeyMap[branch] || `${branch}Branch`;
      setNodes(prev => insertIntoBranchRecursive(prev, parentNodeId, branchKey, branchInsertIndex, newNode));
    } else if (insertIndex !== null) {
      setNodes(prev => {
        const copy = [...prev];
        copy.splice(insertIndex, 0, newNode);
        return copy;
      });
    } else {
      setNodes(prev => [...prev, newNode]);
    }

    setSelectedNodeId(newNode.id);
    setPropertiesPanelOpen(true);
    showToast('success', 'Node Added', `${nodeConfig.label} added to workflow`);
  };

  // Toggle add button at a specific edge
  const handleAddButtonClick = buttonId => {
    if (activeAddButtonId === buttonId) {
      setActionsPanelOpen(false);
      setActiveAddButtonId(null);
    } else {
      setPropertiesPanelOpen(false);
      setSelectedNodeId(null);
      setExecutionsDrawer(prev => ({ ...prev, open: false }));
      setActiveAddButtonId(buttonId);
      setActionsPanelOpen(true);
    }
  };

  const requestDeleteNode = nodeId => {
    const node = findNodeById(nodeId);
    const impact = getCascadeDeleteImpact(nodes, nodeId);
    const cascade = Boolean(impact?.cascade);

    setDeletePending({
      nodeId,
      cascade,
      followingCount: cascade ? impact.followingCount : 0,
      message: cascade
        ? getCascadeDeleteConfirmMessage(node?.type, impact.followingCount)
        : 'Are you sure you want to delete this node?',
    });
    setConfirmingDelete(true);
  };

  const cancelDeleteNode = () => {
    setConfirmingDelete(false);
    setDeletePending(null);
  };

  const confirmDeleteNode = () => {
    if (!deletePending) return;
    const { nodeId, cascade, followingCount } = deletePending;
    deleteNode(nodeId, {
      cascade,
      removedCount: cascade ? followingCount + 1 : 1,
    });
  };

  // Delete node (works at any depth); cascade for send_connection / send_email with steps below
  const deleteNode = (nodeId, { cascade = false, removedCount = 1 } = {}) => {
    const deletedNode = findNodeById(nodeId);
    const pruned = cascade
      ? removeNodeAndFollowing(nodes, nodeId)
      : removeNodeRecursive(nodes, nodeId);
    const nextNodes = pruneOrphanDependentNodes(pruned, deletedNode?.type);
    setNodes(nextNodes);
    if (!findNodeInList(nextNodes, selectedNodeId)) {
      setSelectedNodeId(null);
      setPropertiesPanelOpen(false);
    }
    setConfirmingDelete(false);
    setDeletePending(null);
    showToast(
      'info',
      removedCount > 1 ? 'Steps Removed' : 'Node Deleted',
      removedCount > 1
        ? `${removedCount} steps have been removed from the workflow`
        : 'The node has been removed',
    );
  };

  // Update node data (works at any depth)
  const updateNodeData = (nodeId, key, value) => {
    setNodes(prev => updateNodeRecursive(prev, nodeId, key, value));
  };

  // ─── Insert variable at cursor position ─────────────────
  const insertVariable = useCallback(
    variable => {
      const textarea = messageTextareaRef.current;
      if (!textarea || !selectedNodeId) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentValue = textarea.value;
      const newValue = currentValue.substring(0, start) + variable + currentValue.substring(end);

      updateNodeData(selectedNodeId, 'message', newValue);
      setVariablesDropdownOpen(false);

      // Restore cursor position after the inserted variable
      setTimeout(() => {
        textarea.focus();
        const newCursorPos = start + variable.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    },
    [selectedNodeId, updateNodeData],
  );

  // ─── Voice Recorder helpers ──────────────────────────────
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/mp4' });
      audioChunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = e => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/mp4' });
        const url = URL.createObjectURL(blob);
        setRecordedAudioUrl(url);
        setIsRecording(false);
        clearInterval(recordingTimerRef.current);

        // Convert to base64 and store in node data
        if (selectedNodeId) {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader.result.split(',')[1];
            updateNodeData(selectedNodeId, 'audioBase64', base64);
            updateNodeData(selectedNodeId, 'audioFilename', `recording_${Date.now()}.webm`);
          };
          reader.readAsDataURL(blob);
        }

        // Stop all mic tracks
        stream.getTracks().forEach(t => t.stop());
      };

      mediaRecorder.start(250); // collect data every 250ms
      setIsRecording(true);
      setRecordingDuration(0);
      setRecordedAudioUrl(null);

      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => {
          if (prev >= 60) {
            // max 60 seconds
            mediaRecorderRef.current?.stop();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      showToast('error', 'Microphone Error', 'Could not access microphone. Please allow mic permissions.');
    }
  }, [selectedNodeId, showToast, updateNodeData]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      // Remove the onstop handler so we don't save anything
      mediaRecorderRef.current.onstop = () => {
        mediaRecorderRef.current.stream?.getTracks().forEach(t => t.stop());
      };
      mediaRecorderRef.current.stop();
    }
    clearInterval(recordingTimerRef.current);
    setIsRecording(false);
    setRecordingDuration(0);
    setRecordedAudioUrl(null);
    audioChunksRef.current = [];
  }, []);

  const removeRecording = useCallback(() => {
    if (recordedAudioUrl) URL.revokeObjectURL(recordedAudioUrl);
    setRecordedAudioUrl(null);
    setRecordingDuration(0);
    if (selectedNodeId) {
      updateNodeData(selectedNodeId, 'audioBase64', null);
      updateNodeData(selectedNodeId, 'audioFilename', null);
    }
  }, [recordedAudioUrl, selectedNodeId, updateNodeData]);

  // Clean up recording on unmount or node deselect
  // We disable exhaustive-deps because we only want this to run once.
  // Adding dependencies (like recordedAudioUrl) would make it run on updates,
  // which could stop the recording or revoke the URL too early.
  // Refs are safe to skip since they don’t trigger re-renders.
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stream?.getTracks().forEach(t => t.stop());
        mediaRecorderRef.current.stop();
      }
      clearInterval(recordingTimerRef.current);
      if (recordedAudioUrl) URL.revokeObjectURL(recordedAudioUrl);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When switching nodes, reset recorder state and reload existing recording
  useEffect(() => {
    // Stop any active recording when switching nodes
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.onstop = () => {
        mediaRecorderRef.current.stream?.getTracks().forEach(t => t.stop());
      };
      mediaRecorderRef.current.stop();
    }
    clearInterval(recordingTimerRef.current);
    setIsRecording(false);
    setRecordingDuration(0);
    if (recordedAudioUrl) URL.revokeObjectURL(recordedAudioUrl);
    setRecordedAudioUrl(null);
  }, [selectedNodeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatRecordingTime = seconds => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Save workflow
  const saveWorkflow = async () => {
    const linkedinProfileIds = Object.entries(activeProfiles)
      .filter(([, isActive]) => isActive)
      .map(([id]) => Number(id));

    const payload = {
      name: workflowName,
      description,
      nodes,
      userLeadIds: Object.keys(selectedLeads).map(Number),
      linkedinProfileIds,
      settings: {
        delayBetweenActions: 5000,
        maxRetries: 3,
        // Persist the schedule so editing it and saving (not just launching) sticks.
        // loadWorkflow reads these same keys back from workflow.settings.
        timezone,
        timeFrom,
        timeTo,
        days: selectedDays,
        startingDate,
        ...(hasSendEmailNode && dailyLimit !== '' && dailyLimit != null
          ? { dailyLimit: Number(dailyLimit), max_leads_per_day: Number(dailyLimit) }
          : {}),
      },
    };

    try {
      const data = workflowId
        ? await automationService.updateWorkflow(workflowId, payload)
        : await automationService.createWorkflow(payload);

      if (data.success) {
        // Guard the id read: a success response without a data payload would otherwise
        // throw and get misreported as "Save Failed" for a save that actually worked.
        if (data.data?.id) setWorkflowId(data.data.id);
        showToast('success', 'Saved', 'Workflow saved as draft');
        return true;
      } else {
        showToast('error', 'Save Failed', data.message || 'Failed to save');
        return false;
      }
    } catch (error) {
      console.error('Save error:', error);
      showToast('error', 'Save Failed', 'An error occurred while saving');
      return false;
    }
  };

  const collectAllNodes = () => {
    const allNodes = [];
    const collectNodes = nodeList => {
      nodeList.forEach(n => {
        allNodes.push(n);
        ALL_BRANCH_KEYS.forEach(k => { if (n.data?.[k]) collectNodes(n.data[k]); });
      });
    };
    collectNodes(nodes);
    return allNodes;
  };

  const focusInvalidNode = nodeId => {
    setSelectedNodeId(nodeId);
    setPropertiesPanelOpen(true);
  };

  const validateSequenceBeforeLaunch = () => {
    const allNodes = collectAllNodes();

    if (allNodes.length === 0) {
      showToast('error', 'Workflow Required', 'Please add at least one action to the workflow');
      return false;
    }

    for (const node of allNodes) {
      const error = getNodeStepValidationError(node, 'launch');
      if (error) {
        showToast('error', error.title, error.message);
        focusInvalidNode(node.id);
        return false;
      }
    }

    return true;
  };

  // Handle next step
  const handleNext = async () => {
    if (currentStep === 'sequence') {
      if (!validateSequenceBeforeLaunch()) return;

      const saved = await saveWorkflow();
      if (!saved) return;
      setCurrentStep('launch');
      showToast('success', 'Ready to Launch', 'Review your automation and launch');
    } else {
      showToast('info', 'Launching', 'Starting automation...');
    }
  };

  // Handle launch workflow
  const handleLaunch = async () => {
    if (!workflowId) {
      showToast('error', 'Cannot Launch', 'Please save the workflow first');
      return;
    }

    if (!validateSequenceBeforeLaunch()) return;

    // Get all active profiles' linkedinProfileIds (DB IDs) for round-robin distribution
    const activeProfileEntries = Object.entries(activeProfiles).filter(([, isActive]) => isActive);

    if (activeProfileEntries.length === 0) {
      showToast('error', 'Cannot Launch', 'Please select a sender LinkedIn profile');
      return;
    }

    // Build array of all active LinkedIn profile IDs (using DB IDs)
    const linkedinProfileIds = activeProfileEntries.map(([profileId]) => profileId);

    // Build contacts from selected leads
    const contacts = Object.values(selectedLeads).map(c => ({
      id: c.id,
      linkedinUrl: c.linkedin_url || c.linkedinUrl || '',
      name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.name || '',
      first_name: c.first_name || '',
      last_name: c.last_name || '',
      profileId: c.profile_id || null,
      userLeadId: c.id,
    }));

    if (contacts.length === 0) {
      showToast('error', 'Cannot Launch', 'Please select at least one lead/contact');
      return;
    }

    if (!selectedDays || selectedDays.length === 0) {
      showToast('error', 'Cannot Launch', 'Please select at least one day');
      return;
    }

    if (
      hasSendEmailNode &&
      emailSchedule?.enabled &&
      (!emailSchedule.days || emailSchedule.days.length === 0)
    ) {
      showToast('error', 'Cannot Launch', 'Please select at least one day');
      return;
    }

    if (
      hasSendEmailNode &&
      emailSchedule?.enabled &&
      !isActiveEmailSchedule(emailSchedule)
    ) {
      showToast(
        'error',
        'Cannot Launch',
        'Configure the email schedule (timezone and times) or disable Send email schedule.',
      );
      return;
    }

    const dailyLimitNum = Number(dailyLimit);
    if (hasSendEmailNode && (!Number.isInteger(dailyLimitNum) || dailyLimitNum < 1 || dailyLimitNum > 200)) {
      showToast(
        'error',
        'Cannot Launch',
        "Daily limit must be an integer between 1 and 200 (e.g. 20)",
      );
      return;
    }

    try {
      showToast('info', 'Launching', `Starting automation for ${contacts.length} contact(s)...`);

      const payload = {
        linkedinProfileIds,
        settings: {
          timezone,
          timeFrom,
          timeTo,
          days: selectedDays,
          startingDate,
          ...(hasSendEmailNode
            ? {
              dailyLimit: dailyLimitNum,
              max_leads_per_day: dailyLimitNum,
            }
            : {}),
        },
      };

      const data = await automationService.executeWorkflow(workflowId, payload);

      if (data.success) {
        setWorkflowStatus('active');
        showToast(
          'success',
          'Launched!',
          `Workflow launched for ${data.data?.totalTargets || contacts.length} contact(s)`,
        );
        // Start fetching stats immediately
        fetchWorkflowStats();
      } else {
        showToast('error', 'Launch Failed', data.message || 'Failed to launch workflow');
      }
    } catch (error) {
      showToast('error', 'Launch Failed', error.message || 'An error occurred while launching');
    }
  };

  const handleResume = async () => {
    if (!workflowId) return;
    try {
      const data = await automationService.resumeWorkflow(workflowId);
      if (data.success) {
        setWorkflowStatus('active');
        showToast('success', 'Resumed', 'Workflow resumed successfully');
        fetchWorkflowStats();
      } else {
        showToast('error', 'Resume Failed', data.message || 'Failed to resume workflow');
      }
    } catch (error) {
      showToast('error', 'Resume Failed', error.message || 'An error occurred');
    }
  };

  // Handle adding leads to a running workflow
  const handleAddLeadsToRunning = async newLeadsMap => {
    if (!workflowId) {
      showToast('error', 'Cannot Add Leads', 'Please save the workflow first');
      return;
    }

    const activeProfileEntries = Object.entries(activeProfiles).filter(([, isActive]) => isActive);

    const linkedinProfileIds = activeProfileEntries.map(([profileId]) => profileId);

    const newContacts = Object.values(newLeadsMap).map(c => ({
      id: c.id,
      linkedinUrl: c.linkedin_url || c.linkedinUrl || '',
      name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.name || '',
      first_name: c.first_name || '',
      last_name: c.last_name || '',
      profileId: c.profile_id || null,
      userLeadId: c.id,
    }));

    if (newContacts.length === 0) return;

    try {
      showToast('info', 'Adding Leads', `Adding ${newContacts.length} lead(s) to running workflow...`);

      const data = await automationService.addLeadsToWorkflow(workflowId, {
        contacts: newContacts,
        linkedinProfileIds,
      });

      if (data.success) {
        // Merge new leads into selectedLeads
        setSelectedLeads(prev => ({ ...prev, ...newLeadsMap }));
        const totalCount = Object.keys({ ...selectedLeads, ...newLeadsMap }).length;
        setLeadList(`${totalCount} lead${totalCount !== 1 ? 's' : ''} selected`);
        showToast('success', 'Leads Added', `${newContacts.length} new lead(s) added and queued`);
        fetchWorkflowStats();
      } else {
        showToast('error', 'Failed', data.message || 'Failed to add leads');
      }
    } catch (error) {
      console.error('Add leads error:', error);
      showToast('error', 'Failed', error.message || 'An error occurred');
    }
  };

  // === Pan handlers ===
  const handleCanvasMouseDown = e => {
    // Only pan on left button, and not when clicking on buttons/inputs
    if (e.button !== 0) return;
    if (
      e.target.closest('button') ||
      e.target.closest('input') ||
      e.target.closest('select') ||
      e.target.closest('textarea')
    )
      return;

    setIsPanning(true);
    panStart.current = { x: e.clientX, y: e.clientY };
    panOffset.current = { x: pan.x, y: pan.y };
    e.preventDefault();
  };

  const handleCanvasMouseMove = useCallback(
    e => {
      if (!isPanning) return;
      const dx = e.clientX - panStart.current.x;
      const dy = e.clientY - panStart.current.y;
      setPan(clampPan({
        x: panOffset.current.x + dx,
        y: panOffset.current.y + dy,
      }));
    },
    [clampPan, isPanning],
  );

  const handleCanvasMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  const handleNodeSelect = useCallback(nodeId => {
    setExecutionsDrawer(prev => ({ ...prev, open: false }));
    setActionsPanelOpen(false);
    setActiveAddButtonId(null);
    setSelectedNodeId(nodeId);
    setConfirmingDelete(false);
    setDeletePending(null);
    if (currentStep === 'sequence') {
      setPropertiesPanelOpen(true);
    } else {
      setPropertiesPanelOpen(false);
    }
  }, [currentStep]);

  // Canvas click to close all panels
  const handleCanvasClick = e => {
    if (!e.target.closest('button') && !e.target.closest('[data-interactive]')) {
      setActionsPanelOpen(false);
      setActiveAddButtonId(null);
      setPropertiesPanelOpen(false);
      setSelectedNodeId(null);
    }
  };

  // Mouse wheel: ctrl/cmd → zoom; otherwise pan the canvas (vertical + horizontal)
  const handleWheel = useCallback(e => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      setZoom(prev => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev + delta)));
      return;
    }
    e.preventDefault();
    setPan(prev => clampPan({
      x: prev.x - e.deltaX,
      y: prev.y - e.deltaY,
    }));
  }, [clampPan]);

  useEffect(() => {
    setPan(prev => clampPan(prev));
  }, [clampPan, nodes]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [handleWheel, currentStep]);

  useEffect(() => {
    if (isPanning) {
      window.addEventListener('mousemove', handleCanvasMouseMove);
      window.addEventListener('mouseup', handleCanvasMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleCanvasMouseMove);
        window.removeEventListener('mouseup', handleCanvasMouseUp);
      };
    }
  }, [isPanning, handleCanvasMouseMove, handleCanvasMouseUp]);

  // Zoom controls
  const zoomIn = () => setZoom(prev => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
  const zoomOut = () => setZoom(prev => Math.max(MIN_ZOOM, prev - ZOOM_STEP));

  // Zoom indicator position (0-100%)
  const zoomPercent = ((zoom - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)) * 100;

  // Get selected node and its config (searches flat array and branches)
  const selectedNode = findNodeById(selectedNodeId);
  const selectedNodeConfig = selectedNode ? ACTION_NODES.find(a => a.type === selectedNode.type) : null;

  const emailSchedule = useMemo(() => {
    if (emailNodes.length === 0) return null;
    return getEmailScheduleFromNode(emailNodes[0]);
  }, [emailNodes]);

  const handleUpdateEmailSchedule = useCallback(
    updates => {
      if (emailNodes.length === 0) return;
      const schedule = {
        ...getEmailScheduleFromNode(emailNodes[0]),
        ...updates,
      };
      emailNodes.forEach(node => {
        updateNodeData(node.id, 'emailSchedule', schedule);
      });
    },
    [emailNodes, updateNodeData],
  );

  const validateCurrentNodeStep = useCallback(() => {
    if (!selectedNode) return true;

    if (selectedNode.type === 'send_voice_message' && isRecording) {
      showToast('error', 'Recording in Progress', 'Stop or cancel the recording before saving.');
      return false;
    }

    const error = getNodeStepValidationError(selectedNode, 'save');
    if (error) {
      showToast('error', error.title, error.message);
      return false;
    }

    return true;
  }, [selectedNode, isRecording, showToast]);

  // Nodes that can enable "Skip if lead already replied" in the properties panel.
  const MESSAGE_TYPES = ['send_message', 'send_voice_message'];
  const SKIP_IF_REPLIED_NODE_TYPES = [...MESSAGE_TYPES, 'send_email'];

  // LinkedIn message / connection-with-note steps that run before the selected node.
  const isPriorMessageNode = (n) =>
    MESSAGE_TYPES.includes(n.type) ||
    (n.type === 'send_connection' && !!(n.data?.message && n.data.message.trim()));

  // Prior outreach on any channel — enables cross-channel skip (e.g. email reply → skip LinkedIn message).
  const isPriorOutreachNode = (n) => isPriorMessageNode(n) || n.type === 'send_email';

  // Walks the tree along the execution path to the target node and returns every
  // node that runs before it: earlier siblings at each level plus the branching
  // node that was entered to reach a nested branch. Handles arbitrary nesting.
  const collectPredecessors = (list, targetId) => {
    const preds = [];
    for (const n of list || []) {
      if (n.id === targetId) return { found: true, preds };
      for (const branchKey of ALL_BRANCH_KEYS) {
        if (n.data?.[branchKey]) {
          const res = collectPredecessors(n.data[branchKey], targetId);
          if (res.found) {
            // n is the branching node — it runs before entering the branch
            return { found: true, preds: [...preds, n, ...res.preds] };
          }
        }
      }
      preds.push(n);
    }
    return { found: false, preds };
  };
  const hasPriorMessageNode = (() => {
    if (!selectedNode || !SKIP_IF_REPLIED_NODE_TYPES.includes(selectedNode.type)) return false;
    const { found, preds } = collectPredecessors(nodes, selectedNode.id);
    if (!found) return false;
    return preds.some(isPriorOutreachNode);
  })();

  return (
    <FlowBuilderContainer>
      {currentStep === 'sequence' && (
        <>
          <FlowBuilderCanvas
            canvasRef={canvasRef}
            isPanning={isPanning}
            handleCanvasMouseDown={handleCanvasMouseDown}
            handleCanvasClick={handleCanvasClick}
            pan={pan}
            zoom={zoom}
            leadList={leadList}
            setLeadsModalOpen={setLeadsModalOpen}
            activeAddButtonId={activeAddButtonId}
            handleAddButtonClick={handleAddButtonClick}
            nodes={nodes}
            actionNodes={VISIBLE_ACTION_NODES}
            selectedNodeId={selectedNodeId}
            onNodeSelect={handleNodeSelect}
            getNodeStats={getNodeStats}
            analyticsData={analyticsData}
            completedExecutions={completedExecutions}
            failedExecutions={failedExecutions}
            runningExecutions={runningExecutions}
            onStatClick={openExecutionsDrawer}
            zoomOut={zoomOut}
            zoomIn={zoomIn}
            zoomPercent={zoomPercent}
          />

          <FlowBuilderActionsPanel
            open={actionsPanelOpen}
            actionNodes={VISIBLE_ACTION_NODES}
            activeAddButtonId={activeAddButtonId}
            nodes={nodes}
            userStatus={user?.data?.status}
            hasLinkedInProfiles={hasLinkedInProfiles}
            hasEmailAccounts={emailAccounts.length > 0}
            addNode={addNode}
            setActionsPanelOpen={setActionsPanelOpen}
            setActiveAddButtonId={setActiveAddButtonId}
          />

          <FlowBuilderPropertiesPanel
            open={propertiesPanelOpen}
            selectedNode={selectedNode}
            selectedNodeConfig={selectedNodeConfig}
            setPropertiesPanelOpen={setPropertiesPanelOpen}
            confirmingDelete={confirmingDelete}
            deleteConfirmMessage={deletePending?.message}
            deleteConfirmWide={Boolean(deletePending?.cascade)}
            onRequestDelete={requestDeleteNode}
            onCancelDelete={cancelDeleteNode}
            onConfirmDelete={confirmDeleteNode}
            hasPriorMessageNode={hasPriorMessageNode}
            messageTextareaRef={messageTextareaRef}
            variablesDropdownOpen={variablesDropdownOpen}
            setVariablesDropdownOpen={setVariablesDropdownOpen}
            messageVariables={MESSAGE_VARIABLES}
            insertVariable={insertVariable}
            messageLimits={MESSAGE_LIMITS}
            updateNodeData={updateNodeData}
            isRecording={isRecording}
            recordingDuration={recordingDuration}
            recordedAudioUrl={recordedAudioUrl}
            audioPlayerRef={audioPlayerRef}
            removeRecording={removeRecording}
            cancelRecording={cancelRecording}
            stopRecording={stopRecording}
            startRecording={startRecording}
            formatRecordingTime={formatRecordingTime}
            emailAccounts={emailAccounts}
            linkedinProfiles={userProfile_data || []}
            onSaveStep={validateCurrentNodeStep}
          />
        </>
      )}

      {currentStep === 'launch' && (
        <FlowBuilderLaunch
          campaignName={workflowName}
          setCampaignName={setWorkflowName}
          description={description}
          setDescription={setDescription}
          timezone={timezone}
          setTimezone={setTimezone}
          timeFrom={timeFrom}
          setTimeFrom={setTimeFrom}
          timeTo={timeTo}
          setTimeTo={setTimeTo}
          selectedDays={selectedDays}
          toggleDay={toggleDay}
          startingDate={startingDate}
          setStartingDate={setStartingDate}
          dailyLimit={dailyLimit}
          setDailyLimit={setDailyLimit}
          showDailyLimitField={hasSendEmailNode}
          userProfile_loading={userProfile_loading}
          userProfile_data={userProfile_data}
          activeProfiles={activeProfiles}
          toggleProfile={toggleProfile}
          isIframeOpen={isIframeOpen}
          setIsIframeOpen={setIsIframeOpen}
          hasEmailNodes={emailNodes.length > 0}
          emailSchedule={emailSchedule}
          onUpdateEmailSchedule={handleUpdateEmailSchedule}
        />
      )}

      {/* Floating Header — always visible */}
      <Header>
        <HeaderLeft>
          <BackButton
            onClick={() => {
              if (currentStep === 'launch') {
                setCurrentStep('sequence');
              } else {
                navigate('/campaign-manager');
              }
            }}>
            <RiArrowLeftSLine />
          </BackButton>
          <TitleSection>
            <WorkflowTitle
              ref={titleInputRef}
              type="text"
              value={workflowName}
              onChange={e => setWorkflowName(e.target.value)}
              placeholder="Workflow name..."
            />
            <EditIcon onClick={() => titleInputRef.current?.focus()}>
              <FiEdit />
            </EditIcon>
          </TitleSection>
        </HeaderLeft>

        <HeaderCenter>
          <ProgressStepper>
            <ClickableStepItem $active={currentStep === 'sequence'} onClick={() => setCurrentStep('sequence')}>
              <span>Sequence</span>
              <span className="dot" />
            </ClickableStepItem>
            <StepLine />
            <ClickableStepItem
              $active={currentStep === 'launch'}
              onClick={() => {
                if (currentStep === 'launch') return;
                if (!validateSequenceBeforeLaunch()) return;
                setCurrentStep('launch');
              }}>
              <span className="dot" />
              <span>Launch</span>
            </ClickableStepItem>
          </ProgressStepper>
        </HeaderCenter>

        <HeaderRight>
          {(workflowStatus === 'active' || workflowStatus === 'failed') && (
            <AddLeadsButton onClick={() => setLeadsModalOpen(true)}>+ Add Leads</AddLeadsButton>
          )}
          {(!workflowId || workflowStatus === 'draft') && (
            <SaveDraftButton onClick={saveWorkflow}>Save As Draft</SaveDraftButton>
          )}
          {currentStep === 'sequence' ? (
            <NextButton onClick={handleNext}>Next</NextButton>
          ) : workflowStatus === 'active' ? (
            <SaveDraftButton onClick={saveWorkflow}>Save</SaveDraftButton>
          ) : workflowStatus === 'paused' ? (
            <LaunchButton onClick={handleResume}>Resume</LaunchButton>
          ) : (
            <LaunchButton
              onClick={handleLaunch}
              disabled={workflowStatus === 'completed'}
              $disabledLook={workflowStatus === 'completed'}>
              {workflowStatus === 'failed' ? 'Relaunch' : 'Launch'}
            </LaunchButton>
          )}
        </HeaderRight>
      </Header>

      {/* Toast Notifications */}
      <ToastContainer>
        {toasts.map(toast => (
          <Toast key={toast.id} $type={toast.type}>
            <ToastContent>
              <div className="title">{toast.title}</div>
              <div className="message">{toast.message}</div>
            </ToastContent>
            <ToastClose onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}>
              <CgClose />
            </ToastClose>
          </Toast>
        ))}
      </ToastContainer>

      {/* Leads Module Modal */}
      {leadsModalOpen && (
        <CenterModal
          title="Upload Contacts"
          open={leadsModalOpen}
          setOpen={setLeadsModalOpen}
          width="1200"
          padding="20px 20px 0">
          <LeadsModalBody>
            <LeadsDetail selectionMode onLeadsSelected={handleLeadsSelected} selectedLeadIds={selectedLeads} />
          </LeadsModalBody>
        </CenterModal>
      )}
      <ExecutionsDrawer
        open={executionsDrawer.open}
        onClose={() => setExecutionsDrawer(s => ({ ...s, open: false }))}
        workflowId={workflowId}
        nodeId={executionsDrawer.nodeId}
        nodeLabel={executionsDrawer.nodeLabel}
        initialStatus={executionsDrawer.status}
      />
    </FlowBuilderContainer>
  );
};

export default FlowBuilder;
